/**
 * AiOverageCard (OCP-583) - operator view + write path for a customer's
 * AI spend posture on the account detail page.
 *
 * Read: allowance used/remaining this month, overage mode + cap, wallet
 * balance. Write: the overage switch + cap - the operator path for
 * text-form customer requests ("bitte aktiviert Overage"); the customer
 * self-service surface in the portal stays the norm. Every change is
 * audit-logged server-side with before/after.
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';

interface OverageState {
  account_id: string;
  overage_mode: 'hard_gate' | 'use_credits';
  monthly_cap_eur_cents: number;
  allowance: {
    month: string;
    grant_microcents: number;
    consumed_microcents: number;
    remaining_microcents: number;
  };
  overage_this_month_microcents: number;
  wallet_balance_microcents: number;
}

function eur(microcents: number): string {
  return `€${(microcents / 1_000_000).toFixed(2)}`;
}

export function AiOverageCard({ customerId }: { customerId: string }) {
  const [state, setState] = useState<OverageState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [capInput, setCapInput] = useState('');

  const load = useCallback(async () => {
    try {
      const s = await api<OverageState>(`/v1/admin/accounts/${customerId}/ai-overage-settings`);
      setState(s);
      setCapInput(String(s.monthly_cap_eur_cents / 100));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? `HTTP ${err.status}` : 'Load failed.');
    }
  }, [customerId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function patch(body: { overage_mode?: 'hard_gate' | 'use_credits'; monthly_cap_eur_cents?: number }) {
    setSaving(true);
    setError(null);
    try {
      await api(`/v1/admin/accounts/${customerId}/ai-overage-settings`, {
        method: 'PATCH',
        body,
      });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? `Save failed (HTTP ${err.status}).` : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  if (!state && !error) return null;

  return (
    <section
      data-testid="ai-overage-card"
      className="mt-6 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm"
    >
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">AI spend posture</h2>
      <p className="mt-1 text-xs text-gray-500">
        Included allowance is consumed first; beyond it the overage switch decides. Changing it
        here is the operator path for a text-form customer request - it is audit-logged; the
        customer normally flips it in the portal (/ai).
      </p>
      {error && (
        <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
      {state && (
        <>
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500">Allowance used</div>
              <div className="font-mono" data-testid="ai-overage-allowance">
                {eur(state.allowance.consumed_microcents)} / {eur(state.allowance.grant_microcents)}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500">Overage this month</div>
              <div className="font-mono">{eur(state.overage_this_month_microcents)}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500">Wallet</div>
              <div className="font-mono">{eur(state.wallet_balance_microcents)}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500">Month</div>
              <div className="font-mono">{state.allowance.month}</div>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-gray-100 dark:border-gray-800 pt-4">
            <label htmlFor="ai-overage-mode" className="text-sm text-gray-700 dark:text-gray-300">
              Overage mode
            </label>
            <select
              id="ai-overage-mode"
              value={state.overage_mode}
              disabled={saving}
              onChange={(e) =>
                void patch({ overage_mode: e.target.value as 'hard_gate' | 'use_credits' })
              }
              className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm"
              data-testid="ai-overage-mode-select"
            >
              <option value="hard_gate">hard_gate (stop at allowance)</option>
              <option value="use_credits">use_credits (wallet overage)</option>
            </select>
            <label htmlFor="ai-overage-cap-admin" className="text-sm text-gray-700 dark:text-gray-300">
              Monthly cap (EUR)
            </label>
            <input
              id="ai-overage-cap-admin"
              type="number"
              min={0}
              step="1"
              value={capInput}
              onChange={(e) => setCapInput(e.target.value)}
              className="w-24 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm"
            />
            <button
              type="button"
              disabled={saving}
              data-testid="ai-overage-cap-save-admin"
              onClick={() => {
                const eurVal = Number(capInput);
                if (!Number.isFinite(eurVal) || eurVal < 0) {
                  setError('Cap must be a non-negative number.');
                  return;
                }
                void patch({ monthly_cap_eur_cents: Math.round(eurVal * 100) });
              }}
              className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
            >
              Save cap
            </button>
          </div>
        </>
      )}
    </section>
  );
}
